import { ChipStatus } from "../../../../shared/ppr-chip-status/ppr-chip-status";

const env = process.env.REACT_APP_ENV as string;

const CI_ENV_NAME = "ci";
const DEV_ENV_NAME = "dev";
const QA_ENV_NAME = "qa";
const UAT_ENV_NAME = "stage"; // US Prod Env
const EJP_PROD_ENV_NAME = "ejpprod";
const NEU_PROD_ENV_NAME = "neuprod";

type UrlCheckItem = {
  target: string;
  display?: string[];
};

const COMMON_URL_CHECK_LIST: UrlCheckItem[] = [
  {
    target: "https://ppr-afd-endpoint.prometric.com",
  },
  {
    target: "https://ehelp.prometric.com",
  },
  {
    target: "https://dc.services.visualstudio.com",
  },
  {
    target: "https://tsock.us1.twilio.com",
  },
  {
    target: "https://sdkgw.us1.twilio.com",
  },
  {
    target: "https://global.vss.twilio.com",
  },
];

const CI_URL_CHECK_LIST: UrlCheckItem[] = [
  {
    target:
      "https://app-proproctor-fraud-model-api-ci.nonprod.proproctor.prometric.com/health",
    display: ["https://app-proproctor-fraud-model-api-ci.nonprod.proproctor.prometric.com"],
  },
  {
    target: "https://apim-eus-nonprod.prometric.com",
  },
  {
    target: "https://dev-fraud-model-signalr.nonprod.proproctor.prometric.com",
  },
  {
    target: "https://dev-candidate-app-core.nonprod.proproctor.prometric.com",
  },
  {
    target: "https://bilpas.prometric-testing.com/secureassess/htmldelivery",
    display: ["https://bilpas.prometric-testing.com"],
  },
];

const DEV_URL_CHECK_LIST: UrlCheckItem[] = [
  {
    target:
      "https://app-proproctor-fraud-model-api-dev.nonprod.proproctor.prometric.com/health",
    display: ["https://app-proproctor-fraud-model-api-dev.nonprod.proproctor.prometric.com"],
  },
  {
    target: "https://apim-eus-nonprod.prometric.com",
  },
  {
    target: "https://bil-fraud-model-signalr.nonprod.proproctor.prometric.com",
  },
  {
    target: "https://bil-candidate-app-core.nonprod.proproctor.prometric.com",
  },
  {
    target: "https://bilpas.prometric-testing.com/secureassess/htmldelivery",
    display: ["https://bilpas.prometric-testing.com"],
  },
];

const QA_URL_CHECK_LIST: UrlCheckItem[] = [
  {
    target:
      "https://app-proproctor-fraud-model-api-qa.nonprod.proproctor.prometric.com/health",
    display: ["https://app-proproctor-fraud-model-api-qa.nonprod.proproctor.prometric.com"],
  },
  {
    target: "https://apim-eus-nonprod.prometric.com",
  },
  {
    target: "https://qa-fraud-model-signalr.nonprod.proproctor.prometric.com",
  },
  {
    target: "https://qa-candidate-app-core.nonprod.proproctor.prometric.com",
  },
  {
    target: "https://pas.prometric-qa.com/secureassess/htmldelivery",
    display: ["https://pas.prometric-qa.com"],
  },
];

const UAT_URL_CHECK_LIST: UrlCheckItem[] = [
  {
    target:
      "https://app-proproctor-fraud-model-api-uat.proproctor.prometric.com/health",
    display: ["https://app-proproctor-fraud-model-api-uat.proproctor.prometric.com"],
  },
  {
    target: "https://apim-eus-prod.prometric.com",
  },
  {
    target: "https://fraud-model-signalr.proproctor.prometric.com",
  },
  {
    target: "https://candidate-app-core.proproctor.prometric.com",
  },
  {
    target: "https://surpass.prometric-testing.com/secureassess/htmldelivery",
    display: ["https://surpass.prometric-testing.com"],
  },
];

const EJP_PROD_URL_CHECK_LIST: UrlCheckItem[] = [
  {
    target:
      "https://app-proproctor-fraud-model-api-ejpprod.jp.proproctor.prometric.com/health",
    display: ["https://app-proproctor-fraud-model-api-ejpprod.jp.proproctor.prometric.com"],
  },
  {
    target: "https://apim-ejp-prod.prometric.com",
  },
  {
    target: "https://fraud-model-signalr.jp.proproctor.prometric.com",
  },
  {
    target: "https://candidate-app-core.jp.proproctor.prometric.com",
  },
  {
    target: "https://pas.prometric-testing.jp/secureassess/htmldelivery",
    display: ["https://pas.prometric-testing.jp"],
  },
];

const NEU_PROD_URL_CHECK_LIST: UrlCheckItem[] = [
  {
    target:
      "https://app-proproctor-fraud-model-api-neuprod.eu.proproctor.prometric.com/health",
    display: ["https://app-proproctor-fraud-model-api-neuprod.eu.proproctor.prometric.com"],
  },
  {
    target: "https://apim-neu-prod.prometric.com",
  },
  {
    target: "https://fraud-model-signalr.eu.proproctor.prometric.com",
  },
  {
    target: "https://candidate-app-core.eu.proproctor.prometric.com",
  },
  {
    target: "https://pas.prometric-testing.eu/secureassess/htmldelivery",
    display: ["https://pas.prometric-testing.eu"],
  },
];

const isReachable = async (url: string) => {
  try {
    await fetch(url, {
      method: "HEAD",
      mode: "no-cors",
    });

    return true;
  } catch (error) {
    console.log("URL is not reachable with error: ", error);
    return false;
  }
};

export const checkURLs = async (): Promise<{
  urlErrorList: string[];
  urlCheckStatus: ChipStatus;
}> => {
  let urls: UrlCheckItem[] = [...COMMON_URL_CHECK_LIST];

  switch (env?.toLowerCase()) {
    case CI_ENV_NAME:
      urls = [...urls, ...CI_URL_CHECK_LIST];
      break;

    case DEV_ENV_NAME:
      urls = [...urls, ...DEV_URL_CHECK_LIST];
      break;

    case QA_ENV_NAME:
      urls = [...urls, ...QA_URL_CHECK_LIST];
      break;

    case UAT_ENV_NAME:
      urls = [...urls, ...UAT_URL_CHECK_LIST];
      break;

    case EJP_PROD_ENV_NAME:
      urls = [...urls, ...EJP_PROD_URL_CHECK_LIST];
      break;

    case NEU_PROD_ENV_NAME:
      urls = [...urls, ...NEU_PROD_URL_CHECK_LIST];
      break;
  }

  const results = await Promise.all(
    urls.map(async (url) => ({
      url,
      reachable: await isReachable(url.target),
    })),
  );

  const urlErrorList: string[] = [];

  results
    .filter((result) => !result.reachable)
    .forEach((item) => {
      if (item.url.display) {
        urlErrorList.push(...item.url.display);
      } else urlErrorList.push(item.url.target);
    });

  return {
    urlCheckStatus: urlErrorList.length > 0 ? "failed" : "passed",
    urlErrorList,
  };
};
